import React from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, Phone } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import { Button } from './ui/button';

const ProductPage = ({ eyebrow, title, tagline, heroImage, monthlyPrice, annualPrice, price, includes, notCovered = [], badge, ctaLabel = 'Get a quote' }) => {
  // Backwards-compat: some callers may still pass `price`. Prefer explicit monthly/annual props.
  const showMonthly = monthlyPrice !== undefined;
  const showAnnual = annualPrice !== undefined;
  return (
    <div className="bg-dhs min-h-screen">
      <Header />
      <main>
        <section className="relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-5 pt-10 md:pt-16 pb-14 md:pb-20 grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white border border-[#1F0068]/10 rounded-full px-3.5 py-1.5 text-xs font-semibold text-[#1F0068] mb-5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FF5C39]" /> {eyebrow}
              </div>
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-[#1F0068] tracking-tight leading-[1.02]">{title}</h1>
              <p className="mt-5 text-lg text-[#1F0068]/70 leading-relaxed max-w-lg">{tagline}</p>
              {(showMonthly || showAnnual || price) && (
                <div className="mt-6 flex flex-wrap items-end gap-6">
                  {showMonthly && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-[#1F0068]/60 font-bold">Monthly</div>
                      <div className="text-3xl font-extrabold text-[#1F0068]">{typeof monthlyPrice === 'string' && monthlyPrice.startsWith('Call') ? monthlyPrice : <>£{monthlyPrice}<span className="text-sm font-medium">/month</span></>}</div>
                    </div>
                  )}
                  {showAnnual && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-[#FF5C39] font-bold">Pay Annually</div>
                      <div className="text-3xl font-extrabold text-[#FF5C39]">{typeof annualPrice === 'string' && (annualPrice.startsWith('Call') || annualPrice.startsWith('From')) ? annualPrice : <>£{annualPrice}<span className="text-sm font-medium">/year</span></>}</div>
                    </div>
                  )}
                  {!showMonthly && !showAnnual && price && (
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm text-[#1F0068]/60">From</span>
                      <span className="text-5xl font-extrabold text-[#1F0068]">£{price}</span>
                      <span className="text-sm text-[#1F0068]/60">/month</span>
                    </div>
                  )}
                </div>
              )}
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link to="/quote"><Button className="rounded-full h-14 px-8 bg-[#1F0068] hover:bg-[#2A0C7A] text-white text-base font-semibold">{ctaLabel} <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
                <a href="tel:01135487557"><Button variant="ghost" className="rounded-full h-14 px-6 text-[#1F0068] hover:bg-white gap-2 text-base font-semibold"><Phone className="w-4 h-4" /> 0113 548 7557</Button></a>
              </div>
              <p className="mt-3 text-xs text-[#1F0068]/60 max-w-lg">Other payment options — please call our customer-service team on <a href="tel:01135487557" className="underline font-semibold">0113 548 7557</a> to discuss.</p>
            </div>
            <div className="relative">
              <div className="absolute -top-6 -right-6 w-52 h-52 rounded-full blob-1 blur-2xl opacity-60 -z-10" />
              <div className="rounded-[36px] overflow-hidden shadow-2xl aspect-[4/3]">
                <img src={heroImage} alt={title} className="w-full h-full object-cover" />
              </div>
              {badge && (
                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl px-5 py-4">
                  <div className="text-xs text-[#1F0068]/60">{badge.top}</div>
                  <div className="text-lg font-bold text-[#1F0068]">{badge.value}</div>
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-6xl mx-auto px-5">
            <div className="grid lg:grid-cols-2 gap-10">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-[#1F0068] tracking-tight">What’s included</h2>
                <ul className="mt-6 space-y-3">
                  {includes.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-[#00b67a]/15 text-[#00b67a] flex items-center justify-center shrink-0 mt-0.5"><Check className="w-3.5 h-3.5" /></span>
                      <span className="text-[#1F0068]/80">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {notCovered.length > 0 && (
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold text-[#1F0068] tracking-tight">What’s not covered</h2>
                  <ul className="mt-6 space-y-3">
                    {notCovered.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-[#1F0068]/70">
                        <span className="w-6 h-6 rounded-full bg-[#1F0068]/10 text-[#1F0068]/60 flex items-center justify-center shrink-0 mt-0.5">–</span>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24">
          <div className="max-w-5xl mx-auto px-5">
            <div className="bg-[#1F0068] text-white rounded-[36px] p-8 md:p-14 text-center relative overflow-hidden">
              <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full blob-1 opacity-25" />
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight relative">Ready to choose a plan?</h2>
              <p className="mt-3 text-white/80 max-w-lg mx-auto relative">Get a personalised quote in under 2 minutes — no credit checks. Plans are maintenance service agreements, not insurance products.</p>
              <div className="mt-6 flex items-center justify-center gap-3 flex-wrap relative">
                <Link to="/quote"><Button className="rounded-full bg-[#FF5C39] hover:bg-[#e0492b] text-white h-12 px-8 font-semibold">{ctaLabel} <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
                <a href="tel:01135487557"><Button variant="outline" className="rounded-full border-white text-white hover:bg-white hover:text-[#1F0068] h-12 px-8 font-semibold">Call 0113 548 7557</Button></a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ProductPage;
